// Runtime API/ID endpoint resolution for the admin dashboard (DEV-1057).
//
// A leaf module with NO heavy imports. It owns (a) the pure host-derivation
// helpers and (b) a mutable store of the endpoints resolved at bootstrap. Both
// `config/env.ts` (which bakes the `API_URL`/`ID_URL` consts) and
// `resolve-endpoints.ts` (the probe) import from here, so the pure helpers can
// be used WITHOUT triggering the const freeze — that freeze must happen only
// after the store is populated (see `index.tsx` bootstrap order).

// Cloudflare Pages branch-preview apex for the admin dashboard (apps/admin). A
// `<slug>.symoditi-react-admin.pages.dev` host is a per-PR preview stand.
// Mirrors the server-side `PREVIEW_ORIGIN_SUFFIXES` / `validatePreviewRedirectUri`.
export const ADMIN_PREVIEW_HOST_SUFFIX = '.symoditi-react-admin.pages.dev';

/**
 * True when `hostname` is a non-empty subdomain directly under the admin's Pages
 * preview apex. The bare apex `symoditi-react-admin.pages.dev` is the production
 * CF alias (not a preview) and is excluded, matching the server's non-empty-label
 * rule.
 */
export function isAdminPreviewHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  if (!host.endsWith(ADMIN_PREVIEW_HOST_SUFFIX)) return false;
  const label = host.slice(0, host.length - ADMIN_PREVIEW_HOST_SUFFIX.length);
  return label.length > 0 && !label.startsWith('.') && !label.endsWith('.');
}

/**
 * On a Pages preview host, the per-PR API stand for the same branch. Both admin
 * and app previews of one PR share the SINGLE stand `symoditi-api-<slug>.fly.dev`
 * (the slug matches the CF Pages branch-alias slug by construction for branches
 * ≤28 chars — see `.github/workflows/preview-api.yml`). Divergent longer branches
 * are caught by the health probe in `resolveAndStoreEndpoints`, which falls back
 * to prod. Returns null off a preview host (label non-empty by `isAdminPreviewHost`).
 */
export function deriveAdminApiStandUrl(hostname: string): string | null {
  if (!isAdminPreviewHost(hostname)) return null;
  const host = hostname.toLowerCase();
  const label = host.slice(0, host.length - ADMIN_PREVIEW_HOST_SUFFIX.length);
  return `https://symoditi-api-${label}.fly.dev`;
}

export interface ResolvedEndpoints {
  apiUrl: string;
  idUrl: string;
}

let resolved: ResolvedEndpoints | null = null;

/** Set by `resolveAndStoreEndpoints` before the app bootstraps. */
export function setResolvedEndpoints(endpoints: ResolvedEndpoints): void {
  resolved = endpoints;
}

/** Read by `config/env.ts` when baking `API_URL`/`ID_URL`. Null off-preview. */
export function getResolvedEndpoints(): ResolvedEndpoints | null {
  return resolved;
}
